import React from 'react';
import PropTypes from 'prop-types';
import { alpha, styled } from '@mui/material/styles';
import { Box, Card, Divider, Typography } from '@mui/material';
import { NavLink } from 'react-router-dom';
import Iconify from '../common/iconify.component';
import Scrollbar from '../common/scrollbar.component';
import { formatNumber } from '../../utils/numbers.util';
import ROUTE_PATH from '../../config/routes.config';

const StyledIcon = styled('div')(({ theme }) => ({
  display: 'flex',
  borderRadius: '12px',
  alignItems: 'center',
  width: theme.spacing(6),
  height: theme.spacing(6),
  justifyContent: 'center',
  marginRight: theme.spacing(2.5),
}));

const CountWidget = ({ title, total, color, icon, navLink, sx }) => (
  <>
    {typeof total === 'number' && (
      <Card
        component={navLink ? NavLink : 'div'}
        to={navLink}
        sx={{
          py: 3,
          px: 3,
          width: '100%',
          height: '100%',
          borderRadius: '20px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          textDecoration: 'none',
          color: (theme) => theme.palette[color].darker,
          border: (theme) =>
            `1px solid ${alpha(theme.palette[color].main, 0.1)}`,
          boxShadow: (theme) =>
            `0 4px 20px 0 ${alpha(theme.palette[color].main, 0.08)}`,
          backgroundImage: (theme) =>
            `linear-gradient(135deg, ${alpha(
              theme.palette[color].main,
              0.04
            )} 0%, ${alpha(theme.palette[color].main, 0.12)} 100%)`,
          transition: (theme) =>
            theme.transitions.create(['transform', 'box-shadow'], {
              duration: theme.transitions.duration.shorter,
            }),
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: (theme) =>
              `0 12px 24px 0 ${alpha(theme.palette[color].main, 0.16)}`,
            cursor: navLink ? 'pointer' : 'default',
          },
          ...sx,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <StyledIcon
            sx={{
              color: (theme) => theme.palette[color].dark,
              bgcolor: (theme) => alpha(theme.palette[color].main, 0.12),
              boxShadow: (theme) =>
                `inset 0 0 0 1px ${alpha(theme.palette[color].main, 0.1)}`,
              m: 0,
            }}
          >
            <Iconify icon={icon} width={28} height={28} />
          </StyledIcon>

          <Typography
            variant="subtitle2"
            sx={{
              opacity: 0.6,
              fontWeight: 600,
              textTransform: 'none',
              letterSpacing: 0.2,
            }}
          >
            {title}
          </Typography>
        </Box>

        <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', mb: 3 }}>
          <Typography
            variant="h2"
            sx={{
              fontWeight: 800,
              fontSize: '2.5rem',
              lineHeight: 1,
              color: (theme) => theme.palette[color].darker,
            }}
          >
            {formatNumber(total || 0)}
          </Typography>
        </Box>

        {navLink && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              fontSize: '0.8125rem',
              fontWeight: 700,
              color: (theme) => theme.palette[color].dark,
              bgcolor: (theme) => alpha(theme.palette[color].main, 0.1),
              width: 'fit-content',
              px: 1.5,
              py: 0.5,
              borderRadius: '8px',
            }}
          >
            Manage{' '}
            <Iconify
              icon="material-symbols:arrow-right-alt-rounded"
              width={18}
            />
          </Box>
        )}
      </Card>
    )}

    {typeof total === 'object' && (
      <Card
        sx={{
          py: 3,
          px: 3,
          width: '100%',
          height: '100%',
          borderRadius: '20px',
          display: 'flex',
          flexDirection: 'column',
          color: (theme) => theme.palette[color].darker,
          border: (theme) =>
            `1px solid ${alpha(theme.palette[color].main, 0.1)}`,
          boxShadow: (theme) =>
            `0 4px 20px 0 ${alpha(theme.palette[color].main, 0.08)}`,
          backgroundImage: (theme) =>
            `linear-gradient(135deg, ${alpha(
              theme.palette[color].main,
              0.04
            )} 0%, ${alpha(theme.palette[color].main, 0.12)} 100%)`,
          ...sx,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5 }}>
          {icon && (
            <StyledIcon
              sx={{
                color: (theme) => theme.palette[color].dark,
                bgcolor: (theme) => alpha(theme.palette[color].main, 0.12),
                width: (theme) => theme.spacing(5.5),
                height: (theme) => theme.spacing(5.5),
                borderRadius: '10px',
              }}
            >
              <Iconify icon={icon} width={24} height={24} />
            </StyledIcon>
          )}
          <Typography
            variant="h6"
            sx={{
              fontWeight: 800,
              color: (theme) => theme.palette[color].darker,
            }}
          >
            {title}
          </Typography>
        </Box>

        <Divider
          sx={{
            mb: 2,
            opacity: 0.15,
            borderColor: (theme) => theme.palette[color].main,
          }}
        />

        <Scrollbar sx={{ flexGrow: 1 }}>
          <Box>
            {total.map((item) => (
              <Typography
                key={item.slug}
                variant="body2"
                sx={{ mb: 1.2, '&:last-child': { mb: 0 } }}
              >
                <NavLink
                  to={`${ROUTE_PATH.CONTENT_CATEGORIES}/${item.slug}`}
                  style={{ textDecoration: 'none' }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      px: 1,
                      py: 0.5,
                      borderRadius: '8px',
                      color: (theme) => theme.palette[color].darker,
                      transition: 'background-color 0.2s',
                      '&:hover': {
                        bgcolor: (theme) =>
                          alpha(theme.palette[color].main, 0.08),
                      },
                    }}
                  >
                    <span style={{ fontWeight: 600 }}>{item.tabName}</span>
                    <span style={{ fontWeight: 800, fontSize: '0.95rem' }}>
                      {formatNumber(item.cnt || 0)}
                    </span>
                  </Box>
                </NavLink>
              </Typography>
            ))}
          </Box>
        </Scrollbar>
      </Card>
    )}
  </>
);

CountWidget.propTypes = {
  title: PropTypes.string.isRequired,
  total: PropTypes.number.isRequired,
  color: PropTypes.string,
  icon: PropTypes.string,
  navLink: PropTypes.string,
  sx: PropTypes.object,
};
CountWidget.defaultProps = {
  color: 'primary',
  icon: '',
  navLink: '',
  sx: {},
};

export default CountWidget;
